import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  CreateWorkOrderObservationRequest,
  UpdateWorkOrderObservationRequest,
} from './WorkOrdersObservations.types';

import {
  listWorkOrderObservations,
  deleteWorkOrderObservation,
  createWorkOrderObservation,
  updateWorkOrderObservation,
} from './WorkOrdersObservations.service';

import { notification } from 'antd';
import { useIntl } from 'react-intl';

const QUERY_KEY = 'workOrders/observations';

export function useWorkOrderObservations(
  workOrderId?: number,
  params?: PageRequest,
) {
  return useQuery(
    [QUERY_KEY, { ...params }],
    () => listWorkOrderObservations(workOrderId, params),
    { enabled: !!workOrderId },
  );
}

export function useCreateWorkOrderObservation() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateWorkOrderObservationRequest) => createWorkOrderObservation(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(QUERY_KEY);
        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.details.observations.create.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.details.observations.create.notifications.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateWorkOrderObservation() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateWorkOrderObservationRequest) => updateWorkOrderObservation(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(QUERY_KEY);
        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.details.observations.update.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.details.observations.update.notifications.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteWorkOrderObservation() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: { workOrderComplementId: number; observationId: number }) =>
      deleteWorkOrderObservation(obj.workOrderComplementId, obj.observationId),
    {
      onSuccess() {
        queryClient.invalidateQueries(QUERY_KEY);
        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.details.observations.remove.success.message',
          }),
          description: intl.formatMessage({
            id: 'workOrders.details.observations.remove.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
